import { useState } from "react";
import { Bell, ChevronDown, FileCheck2, TriangleAlert } from "lucide-react";

import { zoneLabel } from "@/components/onboarding/lineHelpers";
import { useDepartments } from "@/hooks/queries/useDepartments";
import { useQualificationStatus } from "@/hooks/queries/useQualificationStatus";
import { useCanAutoQualify } from "@/hooks/useCanAutoQualify";
import { describeLineAlerts, describeLineQualification } from "@/lib/line-alerts";
import { cn } from "@/lib/utils";
import type { ProfileItem, SearchProfile, UserInterest } from "@/types/domain";

/** "a", "a y b", "a, b y c". */
function joinAnd(parts: string[]): string {
  if (parts.length <= 1) return parts[0] ?? "";
  return `${parts.slice(0, -1).join(", ")} y ${parts.at(-1)}`;
}

/** "1 distrito", "3 distritos". */
function plural(count: number, singular: string, pluralForm: string): string {
  return `${count} ${count === 1 ? singular : pluralForm}`;
}

/**
 * "Avisos de esta línea": qué licitaciones le avisamos, a quiénes y si se cruzan
 * con los documentos de la empresa. Va en la tarjeta de la línea, debajo de sus filtros.
 */
export function LineAlertsCard({
  profile,
  interests,
  items,
}: {
  profile: SearchProfile;
  interests: UserInterest[];
  items: ProfileItem[];
}) {
  const { data: departments = [] } = useDepartments();
  const canAutoQualify = useCanAutoQualify();
  const { data: qualificationStatus } = useQualificationStatus();

  const districtIds = profile.searchprofile_districtids ?? [];
  const provinceIds = profile.searchprofile_provinceids ?? [];
  const departmentIds = profile.searchprofile_departmentids ?? [];
  const zone =
    districtIds.length > 0
      ? plural(districtIds.length, "distrito", "distritos")
      : provinceIds.length > 0
        ? plural(provinceIds.length, "provincia", "provincias")
        : departmentIds.length > 0
          ? zoneLabel(departments, departmentIds)
          : "";

  const entityIds = profile.searchprofile_procuringentityids ?? [];
  const entity =
    entityIds.length === 1 && profile.procuringEntities?.[0]
      ? profile.procuringEntities[0].procuringentity_name
      : entityIds.length > 0
        ? plural(entityIds.length, "entidad", "entidades")
        : "";

  const [showTerms, setShowTerms] = useState(false);
  const { sentence, warning, termGroups } = describeLineAlerts({
    profile,
    resourceTerms: interests.filter((i) => i.active ?? true).map((i) => i.keyword),
    itemTerms: items.filter((i) => i.active ?? true).map((i) => i.keyword),
    zone,
    entity,
  });

  // Solo con cruce automático (Comercial a más) y los documentos esenciales: sin ellos no se cruza nada.
  const qualification =
    canAutoQualify && qualificationStatus?.missingEssentialDocuments.length === 0
      ? describeLineQualification({
          profile,
          blockedReason:
            qualificationStatus.lines.find(
              (line) => line.searchProfileId === profile.searchprofile_id,
            )?.blockedReason ?? null,
        })
      : null;

  const assignees = profile.assignedUsers ?? [];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-3">
        <Bell className="mt-0.5 size-4 shrink-0 text-primary" />
        <div className="flex flex-col gap-1.5 text-sm">
          <span className="font-semibold">Avisos de esta línea</span>
          {sentence && <p className="text-foreground/80">{sentence}</p>}
          {sentence && termGroups.length > 0 && (
            <div>
              <button
                type="button"
                onClick={() => setShowTerms((open) => !open)}
                aria-expanded={showTerms}
                className="flex cursor-pointer items-center gap-1 text-xs font-semibold text-primary hover:underline"
              >
                {showTerms ? "Ocultar los términos" : "Ver todos los términos"}
                <ChevronDown
                  className={cn("size-3.5 transition-transform", showTerms && "rotate-180")}
                />
              </button>
              {showTerms &&
                termGroups.map((group) => (
                  <div key={group.label} className="mt-2">
                    {termGroups.length > 1 && (
                      <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                        {group.label}
                      </div>
                    )}
                    <div className="flex flex-wrap gap-1.5">
                      {group.terms.map((term) => (
                        <span
                          key={term}
                          className="rounded-full border bg-muted/50 px-2 py-0.5 text-xs text-foreground/80"
                        >
                          {term}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
            </div>
          )}
          {/* La línea avisa una vez; seguirla es lo que da el resto (gancho para guardarla). */}
          {sentence && (
            <p className="text-muted-foreground">
              Si la guardas, te seguimos avisando de todo lo que le pase.
            </p>
          )}
          {warning && (
            <p className="flex gap-1.5 text-amber-700">
              <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
              {warning}
            </p>
          )}
          {/* Quien la ve está entre los asignados: solo se nombra cuando son varios. */}
          {sentence && assignees.length > 1 && (
            <p className="text-muted-foreground">
              Les llega a {joinAnd(assignees.map((user) => user.name))}.
            </p>
          )}
        </div>
      </div>
      {qualification && (
        <div className="flex gap-3">
          <FileCheck2 className="mt-0.5 size-4 shrink-0 text-emerald-600" />
          <div className="flex flex-col gap-1.5 text-sm">
            <span className="font-semibold">Cruce con tus documentos</span>
            <p className="text-foreground/80">{qualification}</p>
          </div>
        </div>
      )}
    </div>
  );
}
